import type {
  BatchConfigEntry,
  BatchContext,
  ConfigureDetailLocationState,
  SelectedSubject,
  TestData,
} from "./testTypes";

const ASSIGN_TEST_STORAGE_KEY = "adAssignTest";
const CONFIGURE_DETAIL_STORAGE_KEY = "adConfigureDetail";

export type ConfigureDetailContext = {
  test_id: string;
  subjects: SelectedSubject[];
};

export const saveAssignTestContext = (test: TestData) => {
  sessionStorage.setItem(ASSIGN_TEST_STORAGE_KEY, JSON.stringify(test));
};

export const loadAssignTestContext = (
  testId: string | undefined
): TestData | null => {
  if (!testId) return null;
  try {
    const stored = sessionStorage.getItem(ASSIGN_TEST_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as TestData;
    return parsed.test_id === testId ? parsed : null;
  } catch {
    return null;
  }
};

export const resolveAssignTest = (
  testId: string | undefined,
  stateTest?: TestData | null
): TestData | null => {
  if (stateTest && testId && stateTest.test_id === testId) {
    return stateTest;
  }
  return loadAssignTestContext(testId);
};

export const saveConfigureDetailContext = (context: ConfigureDetailContext) => {
  sessionStorage.setItem(CONFIGURE_DETAIL_STORAGE_KEY, JSON.stringify(context));
};

export const loadConfigureDetailContext = (
  testId: string | undefined
): ConfigureDetailContext | null => {
  if (!testId) return null;
  try {
    const stored = sessionStorage.getItem(CONFIGURE_DETAIL_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as ConfigureDetailContext;
    return parsed.test_id === testId ? parsed : null;
  } catch {
    return null;
  }
};

export const resolveConfigureDetailState = (
  testId: string | undefined,
  state?: ConfigureDetailLocationState | null
): ConfigureDetailLocationState | null => {
  if (state?.test && testId && state.test.test_id === testId) {
    return state;
  }
  const stored = loadConfigureDetailContext(testId);
  const test = loadAssignTestContext(testId);
  if (!test || !stored) return null;
  return {
    test,
    subjects: stored.subjects,
    batchConfigs: [],
  };
};

export type AssignStudentsLocationState = {
  test?: TestData;
  batchContext?: BatchContext;
  batchConfigs?: BatchConfigEntry[];
  subjects?: SelectedSubject[];
  slot_id?: number;
};

export type ReportFiltersContext = {
  reportTrackId: string;
  reportCourseId: string;
  reportBatchId: string;
  reportSubjectFilter: string;
  reportTestType: string;
  selectedLiveOrCompleted: string;
};

const REPORT_FILTERS_STORAGE_KEY = "adReportFilters";

export const EMPTY_REPORT_FILTERS: ReportFiltersContext = {
  reportTrackId: "",
  reportCourseId: "",
  reportBatchId: "",
  reportSubjectFilter: "",
  reportTestType: "",
  selectedLiveOrCompleted: "",
};

export const saveReportFiltersContext = (filters: ReportFiltersContext) => {
  sessionStorage.setItem(REPORT_FILTERS_STORAGE_KEY, JSON.stringify(filters));
};

export const loadReportFiltersContext = (): ReportFiltersContext | null => {
  try {
    const stored = sessionStorage.getItem(REPORT_FILTERS_STORAGE_KEY);
    if (!stored) return null;
    return { ...EMPTY_REPORT_FILTERS, ...JSON.parse(stored) };
  } catch {
    return null;
  }
};

export const buildReportFiltersFromSearchParams = (
  searchParams: URLSearchParams
): ReportFiltersContext | null => {
  const trackId = searchParams.get("track_id");
  const courseId = searchParams.get("course_id");
  const batchId = searchParams.get("batch_id");
  const subject = searchParams.get("subject");
  const testType = searchParams.get("test_type");
  const status = searchParams.get("status");

  if (!trackId && !courseId && !batchId && !subject && !testType && !status) {
    return null;
  }

  return {
    reportTrackId: trackId ?? "",
    reportCourseId: courseId ?? "",
    reportBatchId: batchId ?? "",
    reportSubjectFilter: subject ?? "",
    reportTestType: testType ?? "",
    selectedLiveOrCompleted: status ?? "",
  };
};

export const resolveReportFilters = (
  searchParams: URLSearchParams
): ReportFiltersContext =>
  buildReportFiltersFromSearchParams(searchParams) ??
  loadReportFiltersContext() ??
  EMPTY_REPORT_FILTERS;
